import React, { useState } from "react";
import "../../css/CRUDModal.css";
import ButtonTriangle from "../../assets/Icons/bthTriangle.svg";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { useForm, Controller } from "react-hook-form";
import { Box, CircularProgress } from "@mui/material";
import CustomAlert from "../../helpers/CustomAlert";
import { axiosPostApi } from "../../helpers/interceptorServices";
import { apiList } from "../../helpers/apiList";

const OrderFilterModal = ({
  show,
  handleClose,
  setOrderData,
  setPageDetails,
  setNoData,
  pageSize,
  currentPage,
  filterData,
  setFilterData,
  handleReset,
}) => {
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { isSubmitting, errors },
  } = useForm({
    defaultValues: {
      ...filterData,
      retailerId: filterData.retailerId || "",
      consigneeName: filterData.consigneeName || "",
      consigneeMobile: filterData.consigneeMobile || "",
      fromDate: filterData.fromDate || "",
      toDate: filterData.toDate || "",
    },
    mode: "onChange",
  });

  const [selectedTab, setSelectedTab] = useState("Retailer Id");
  const [loading, setLoading] = useState(false);
  const loggedInUserName = sessionStorage.getItem("username");

  const handleTabChange = (e, tabName) => {
    e.preventDefault();
    setSelectedTab(tabName);
  };

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  };

  const onSubmit = (data) => {
    setFilterData(data);
    fetchOrderData(currentPage, data);
  };

  const fetchOrderData = async (page, formData) => {
    setLoading(true);
    try {
      const requestBody = {
        username: loggedInUserName,
        retailerId: formData.retailerId || "",
        consigneeName: formData.consigneeName || "",
        consigneeMobile: formData.consigneeMobile || "",
        fromDate: formatDate(formData.fromDate),
        toDate: formatDate(formData.toDate),
      };

      const response = await axiosPostApi(
        `${apiList.FETCH_ORDER_MASTER_DATA}?pageSize=${pageSize}&pageNo=${page}`,
        requestBody
      );

      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        setPageDetails(response?.data?.pageDetails);
        const modifiedData = modifyStructure(response?.data?.data);
        setOrderData(modifiedData);
        setNoData(modifiedData?.length === 0);
        handleClose();
      } else {
        CustomAlert({
          title: "Failed",
          text:
            response?.data?.message ||
            "There was an issue processing the request.",
          icon: "error",
          confirmButtonText: "Retry",
        });
        setOrderData([]);
        setNoData(true);
      }
    } catch (error) {
      CustomAlert({
        title: "Error",
        text: "An error occurred while fetching the data. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    } finally {
      setLoading(false);
    }
  };

  const modifyStructure = (response) => {
    return response?.map((order, index) => ({
      ...order,
      id: index + 1,
    }));
  };

  const onReset = () => {
    reset({
      retailerId: "",
      consigneeName: "",
      consigneeMobile: "",
      fromDate: "",
      toDate: "",
    });
    setFilterData({
      retailerId: "",
      consigneeName: "",
      consigneeMobile: "",
      fromDate: "",
      toDate: "",
    });
    handleReset();
  };

  return (
    <>
      {show && (
        <div
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1060,
          }}
        >
          <div className="modal-dialog" role="document">
            <div
              className="modal-content"
              style={{
                width: "40vw",
                borderRadius: "24px",
                padding: "24px",
                backgroundColor: "#FFF",
                border: "1px solid rgba(0, 0, 0, 0.175)",
              }}
            >
              <div
                className="modal-header"
                style={{
                  padding: "1rem 1rem",
                  borderBottom: "1px solid #dee2e6",
                  justifyContent: "space-between",
                  display: "flex",
                }}
              >
                <h5 className="modal-title main-title">Filter</h5>
                <HighlightOffIcon
                  onClick={handleClose}
                  style={{ cursor: "pointer" }}
                />
              </div>
              <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                <div
                  className="scoped-modal modal-body"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <div className="tab-container">
                    <ul className="tab-list">
                      {[
                        "Retailer Id",
                        "Consignee Name",
                        "Consignee Mobile",
                        "Created Date",
                      ].map((tab) => (
                        <div className="tab-wrapper" key={tab}>
                          <li
                            className={
                              selectedTab === tab
                                ? "tab-item selected"
                                : "tab-item"
                            }
                          >
                            <button
                              className={`tab-button ${
                                selectedTab === tab ? "active" : ""
                              }`}
                              onClick={(e) => handleTabChange(e, tab)}
                            >
                              {tab}
                            </button>
                          </li>
                          {selectedTab === tab && (
                            <img
                              src={ButtonTriangle}
                              alt="tri"
                              className="tab-triangle"
                            />
                          )}
                        </div>
                      ))}
                    </ul>

                    <div className="tab-content">
                      {selectedTab === "Retailer Id" && (
                        <Controller
                          name="retailerId"
                          control={control}
                          render={({ field }) => (
                            <div className="dateField">
                              <div className="dateInputWrapper">
                                <label className="dateLabel">Retailer ID</label>
                                <input
                                  {...field}
                                  type="text"
                                  placeholder="Enter Retailer ID"
                                  className="dateInput"
                                  maxLength={20}
                                  pattern="[A-Za-z0-9]+"
                                  onChange={(e) => {
                                    const val = e.target.value.replace(
                                      /[^a-zA-Z0-9]/g,
                                      ""
                                    );
                                    field.onChange(val);
                                    setFilterData((prev) => ({
                                      ...prev,
                                      retailerId: val,
                                    }));
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        />
                      )}

                      {selectedTab === "Consignee Name" && (
                        <Controller
                          name="consigneeName"
                          control={control}
                          rules={{
                            pattern: {
                              value: /^[a-zA-Z\s]*$/,
                              message:
                                "Consignee Name can only contain letters and spaces",
                            },
                          }}
                          render={({ field }) => (
                            <div className="dateField">
                              <div className="dateInputWrapper">
                                <label className="dateLabel">
                                  Consignee Name
                                </label>
                                <input
                                  {...field}
                                  type="text"
                                  placeholder="Enter Consignee Name"
                                  className="dateInput"
                                  onChange={(e) => {
                                    field.onChange(e);
                                    setFilterData((prev) => ({
                                      ...prev,
                                      consigneeName: e.target.value,
                                    }));
                                  }}
                                />
                                {errors.consigneeName && (
                                  <span className="error-text">
                                    {errors.consigneeName.message}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        />
                      )}

                      {selectedTab === "Consignee Mobile" && (
                        <Controller
                          name="consigneeMobile"
                          control={control}
                          rules={{
                            minLength: {
                              value: 10,
                              message: "Mobile No must be at least 10 digits",
                            },
                            pattern: {
                              value: /^[6-9]\d{9}$/,
                              message:
                                "Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9",
                            },
                            validate: {
                              noRepeats: (value) =>
                                !/^([0-9])\1*$/.test(value) ||
                                "Repeated digits are not allowed",
                            },
                          }}
                          render={({ field }) => (
                            <div className="dateField">
                              <div className="dateInputWrapper">
                                <label className="dateLabel">
                                  Consignee Mobile
                                </label>
                                <input
                                  {...field}
                                  type="text"
                                  placeholder="Enter Consignee Mobile"
                                  className="dateInput"
                                  maxLength={10}
                                  onChange={(e) => {
                                    const val = e.target.value.replace(
                                      /[^0-9]/g,
                                      ""
                                    );
                                    field.onChange(val);
                                    setFilterData((prev) => ({
                                      ...prev,
                                      consigneeMobile: val,
                                    }));
                                  }}
                                />
                                {errors.consigneeMobile && (
                                  <span className="error-text">
                                    {errors.consigneeMobile.message}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        />
                      )}

                      {selectedTab === "Created Date" && (
                        <div
                          className="dateGrid"
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            gap: "1rem",
                            alignItems: "flex-start",
                          }}
                        >
                          <Controller
                            name="fromDate"
                            control={control}
                            rules={{
                              validate: {
                                validDate: (value) =>
                                  !value ||
                                  !isNaN(Date.parse(value)) ||
                                  "Invalid From Date",
                                notFuture: (value) =>
                                  !value ||
                                  new Date(value) <= new Date() ||
                                  "From Date cannot be in the future",
                                beforeToDate: (value) =>
                                  !value ||
                                  !filterData.toDate ||
                                  new Date(value) <=
                                    new Date(filterData.toDate) ||
                                  "From Date must be before To Date",
                                requiredIfToDate: (value, formValues) =>
                                  !formValues.toDate ||
                                  !!value ||
                                  "From Date is required when To Date is selected",
                              },
                            }}
                            render={({ field }) => (
                              <div className="dateField" style={{ flex: 1 }}>
                                <label className="dateLabel">From Date</label>
                                <div className="dateInputWrapper">
                                  <input
                                    {...field}
                                    type="date"
                                    className="dateInput"
                                    max={getTodayDate()}
                                    onChange={(e) => {
                                      field.onChange(e);
                                      setFilterData((prev) => ({
                                        ...prev,
                                        fromDate: e.target.value,
                                      }));
                                    }}
                                  />
                                  {errors.fromDate && (
                                    <span className="error-text">
                                      {errors.fromDate.message}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          />
                          <Controller
                            name="toDate"
                            control={control}
                            rules={{
                              validate: {
                                validDate: (value) =>
                                  !value ||
                                  !isNaN(Date.parse(value)) ||
                                  "Invalid To Date",
                                notFuture: (value) =>
                                  !value ||
                                  new Date(value) <= new Date() ||
                                  "To Date cannot be in the future",
                                afterFromDate: (value) =>
                                  !value ||
                                  !filterData.fromDate ||
                                  new Date(value) >=
                                    new Date(filterData.fromDate) ||
                                  "To Date must be after From Date",
                                requiredIfFromDate: (value, formValues) =>
                                  !formValues.fromDate ||
                                  !!value ||
                                  "To Date is required when From Date is selected",
                              },
                            }}
                            render={({ field }) => (
                              <div className="dateField" style={{ flex: 1 }}>
                                <label className="dateLabel">To Date</label>
                                <div className="dateInputWrapper">
                                  <input
                                    {...field}
                                    type="date"
                                    className="dateInput"
                                    max={getTodayDate()}
                                    onChange={(e) => {
                                      field.onChange(e);
                                      setFilterData((prev) => ({
                                        ...prev,
                                        toDate: e.target.value,
                                      }));
                                    }}
                                  />
                                  {errors.toDate && (
                                    <span className="error-text">
                                      {errors.toDate.message}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div
                  className="scoped-modal"
                  style={{
                    display: "flex",
                    justifyContent: "end",
                    padding: "12px",
                    paddingBottom: "0px",
                    gap: "16px",
                    borderTop: "1px solid #dee2e6",
                  }}
                >
                  <div className="buttonContainer">
                    <button
                      type="button"
                      className="btn btn-back"
                      onClick={onReset}
                    >
                      Reset
                    </button>
                  </div>
                  <div className="buttonContainer">
                    <button
                      type="submit"
                      className="btn btn-submit"
                      disabled={isSubmitting}
                    >
                      {loading ? (
                        <CircularProgress
                          style={{ width: "20px", height: "20px" }}
                        />
                      ) : (
                        "Submit"
                      )}
                    </button>
                  </div>
                </div>
              </Box>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrderFilterModal;
